import {useCallback, useState} from 'react';
import {useAppDispatch} from '../../store/hooks';
import {useToast} from '../Toast';
import {ApiError} from '../../lib/apiError';
import {releaseInstant, IN_FLIGHT_RETRY_SECONDS, PendingTranscription} from '../../lib/pendingTranscriptions';
import {formatDuration} from '../../lib/audioMetadata';
import {WorkspaceRow} from '../../store/selectors/workspace.selectors';
import {
  startTranscription,
  fetchTranscriptions,
  pollTranscriptionStatus,
  DUPLICATE_SUBMIT_COOLDOWN_MS,
} from '../../store/slices/transcriptionsThunks';
import {fetchTranscriptionUsage} from '../../store/slices/usageThunks';
import {
  enqueuePendingTranscription,
  removePendingTranscription,
} from '../../store/slices/pendingTranscriptionsSlice';

/** What a refused job needs to be replayed later, held while the user decides. */
interface RefusedJob {
  error: ApiError;
  row: WorkspaceRow;
  outputFileName: string;
  languageCode?: string;
  /** Resolved once, here, so the dialog and the queued entry count down to the same instant. */
  releaseAt: string;
}

interface UseTranscriptionLaunchOptions {
  projectId: string | null;
  /** The 402 wall. Shared with the upload queue, which raises the same modal. */
  onPaymentRequired: (error: ApiError) => void;
  /** Closes the confirm dialog once the job is away — or once it has been queued instead. */
  onLaunched: () => void;
}

/**
 * Starting a transcription and everything the server can answer with.
 *
 * Extracted from `workspace.container` because the three refusals branch three different ways and
 * inlining them pushed the container past the file-length rule. The branches, all on `reason`
 * rather than on the status:
 *
 *   - **402** (`file_too_long`, `period_limit_exceeded`) — a paywall. Money fixes it, so it goes to
 *     `UpgradeRequiredModal`.
 *   - **429 `session_limit_exceeded`** — the rate throttle. A wait no plan shortens, so it goes to
 *     `SessionLimitModal`, which offers to hold the job until the session resets instead of selling
 *     anything.
 *   - **429 `too_many_in_flight`** — three jobs are already running. A user submitting a batch of
 *     episodes hits this routinely, so it is not a dialog to dismiss or an error to fix: the job is
 *     queued automatically (same `pendingTranscriptions` queue the session limit uses) and an info
 *     toast says so. `pendingTranscriptions.container` retries it on a short timer and moves on the
 *     moment one of the three running jobs reaches a terminal status.
 *   - **409 `duplicate_in_flight`** — this exact file is already running (see the thunk's own doc).
 *     Adopt the job it names and resume polling it rather than treating this as a failure.
 *
 * `isStarting` disables Start for `DUPLICATE_SUBMIT_COOLDOWN_MS` from the click, not from the
 * response — see the constant's doc in `transcriptionsThunks`.
 */
export function useTranscriptionLaunch({
  projectId,
  onPaymentRequired,
  onLaunched,
}: UseTranscriptionLaunchOptions) {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();

  const [isStarting, setIsStarting] = useState(false);
  const [refused, setRefused] = useState<RefusedJob | null>(null);

  const start = useCallback(async (
    row: WorkspaceRow,
    {outputFileName, languageCode}: {outputFileName: string; languageCode?: string}
  ) => {
    // Belt-and-braces alongside the thunk's own lock: a click that lands before this state update
    // has painted must not even reach `dispatch`.
    if (isStarting) {
      return;
    }

    setIsStarting(true);
    // Ignore how long the request actually takes to settle — `/transcribe` answers in well under a
    // second, which reopened the button almost as fast as it closed it and left plenty of room for
    // a human double-click. A flat cooldown timed from the click, not from the response, is what
    // actually holds the button disabled long enough to matter. Same constant the thunk's own lock
    // uses, so both give up at the same instant.
    setTimeout(() => setIsStarting(false), DUPLICATE_SUBMIT_COOLDOWN_MS);

    const result = await dispatch(startTranscription({
      audioId: row.audio.id,
      outputFileName,
      ...(projectId ? {projectId} : {}),
      ...(languageCode ? {languageCode} : {}),
    }));

    if (startTranscription.fulfilled.match(result)) {
      // Show what the server actually created — it sanitises and de-duplicates the name.
      showToast(`Transcribing — the CSV will be saved as ${result.payload.name}.csv`, 'success');
      // The job is accepted and has already reserved its seconds against both meters, so re-read
      // the server's numbers rather than leaving the header claiming time that is now spent.
      dispatch(fetchTranscriptionUsage());
      onLaunched();
      dispatch(fetchTranscriptions(projectId || 'all'));
      return;
    }

    const error = result.payload ?? null;

    if (!error) {
      return;
    }

    if (error.status === 402) {
      onLaunched();
      onPaymentRequired(error);
      return;
    }

    // The click-guard in `startTranscription` only sees this tab's in-memory state, so it cannot
    // catch a reload that lost the lock mid-flight, a second tab, or this exact file already being
    // released from the pending queue at the same moment. The server knows better: this is not a
    // failure, it is a stale client catching up — adopt the job it names instead of erroring out.
    if (error.status === 409 && error.reason === 'duplicate_in_flight') {
      onLaunched();
      if (error.transcriptionId) {
        dispatch(pollTranscriptionStatus(error.transcriptionId));
      }
      showToast(`${row.audio.name} is already transcribing — resuming.`, 'info');
      return;
    }

    if (error.status === 429 && error.reason === 'session_limit_exceeded') {
      onLaunched();
      setRefused({
        error,
        row,
        outputFileName,
        languageCode,
        releaseAt: releaseInstant(error.sessionExpiresAt, error.retryAfterSeconds),
      });
      return;
    }

    if (error.status === 429 && error.reason === 'too_many_in_flight') {
      // Expected during normal batching — queue it rather than dropping it, the same way a session
      // limit is queued, so the rest of a batch keeps moving without the user having to notice a
      // slot freed up and re-click Transcribe themselves.
      onLaunched();

      const entry: PendingTranscription = {
        id: `pending-${row.audio.id}-${Date.now()}`,
        audioId: row.audio.id,
        audioName: row.audio.name,
        outputFileName,
        ...(projectId ? {projectId} : {}),
        ...(languageCode ? {languageCode} : {}),
        durationSeconds: row.audio.duration ?? 0,
        releaseAt: new Date(Date.now() + IN_FLIGHT_RETRY_SECONDS * 1000).toISOString(),
        queuedAt: new Date().toISOString(),
        reason: 'in_flight',
      };

      dispatch(enqueuePendingTranscription(entry));
      showToast(
        `${row.audio.name} is queued — ${error.inFlight ?? 'too many'} transcriptions are already `
        + 'running. It will start on its own when one finishes.',
        'info',
        6000
      );
      return;
    }

    if (error.status === 429) {
      // The hourly rate limit — a third kind of wait, with its own number on it.
      showToast(
        error.resetIn
          ? `${error.message} Try again in ${formatDuration(error.resetIn)}.`
          : error.message,
        'error',
        6000
      );
      return;
    }

    showToast(error.message, 'error', 6000);
  }, [dispatch, projectId, showToast, onLaunched, onPaymentRequired, isStarting]);

  /** Hold the refused job until the session resets. `pendingTranscriptions.container` releases it. */
  const queueRefused = useCallback(() => {
    if (!refused) {
      return;
    }

    const entry: PendingTranscription = {
      id: `pending-${refused.row.audio.id}-${Date.now()}`,
      audioId: refused.row.audio.id,
      audioName: refused.row.audio.name,
      outputFileName: refused.outputFileName,
      ...(projectId ? {projectId} : {}),
      ...(refused.languageCode ? {languageCode: refused.languageCode} : {}),
      durationSeconds: refused.row.audio.duration ?? 0,
      releaseAt: refused.releaseAt,
      queuedAt: new Date().toISOString(),
      reason: 'session_limit',
    };

    dispatch(enqueuePendingTranscription(entry));
    showToast(`${entry.audioName} is queued — it will start when your session resets`, 'success', 6000);
    setRefused(null);
  }, [refused, projectId, dispatch, showToast]);

  const cancelQueued = useCallback((entry: PendingTranscription) => {
    dispatch(removePendingTranscription(entry.id));
    showToast(`${entry.audioName} is no longer queued`, 'info');
  }, [dispatch, showToast]);

  return {
    isStarting,
    start,
    /** The refused job behind `SessionLimitModal`, or null. */
    refused,
    dismissRefused: useCallback(() => setRefused(null), []),
    queueRefused,
    cancelQueued,
  };
}
